/**
 * Drop zone component for file uploads
 */

import type { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useDragAndDrop } from '@/hooks/use-drag-and-drop';

export interface DropZoneProps {
  onDrop: (files: File[]) => void;
  onClick?: () => void;
  disabled?: boolean;
  acceptedFileTypes?: string[];
  className?: string;
  contentClassName?: string;
  children?: ReactNode;
}

export function DropZone({
  onDrop,
  onClick,
  disabled,
  acceptedFileTypes,
  className,
  contentClassName,
  children
}: DropZoneProps) {
  const { isDragging, getRootProps } = useDragAndDrop({
    onDrop,
    acceptedFileTypes,
    disabled
  });

  const handleClick = (e: React.MouseEvent) => {
    if (!disabled && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <Card
      {...getRootProps()}
      onClick={handleClick}
      className={cn(
        'border-2 border-dashed transition-all duration-200',
        isDragging && !disabled && 'border-primary bg-primary/5',
        disabled && 'cursor-not-allowed opacity-50',
        !disabled && 'hover:border-primary/50 cursor-pointer',
        className
      )}
    >
      <div className={cn('p-4 text-center', contentClassName)}>
        {children || (
          <>
            <Upload
              className={cn(
                'text-muted-foreground mx-auto mb-2 h-3.5 w-3.5 transition-colors',
                isDragging && !disabled && 'text-primary'
              )}
            />
            <p className="mb-2 text-xs leading-4 font-medium text-[color:var(--grey-45)]">
              {isDragging ? 'Drop files here' : 'Drag and drop files here'}
            </p>
            <p className="text-muted-foreground text-xs">or click to browse</p>
            {acceptedFileTypes && acceptedFileTypes.length > 0 && (
              <p className="text-muted-foreground text-12px mt-2 font-normal">
                Accepted: {acceptedFileTypes.join(', ')}
              </p>
            )}
          </>
        )}
      </div>
    </Card>
  );
}
